import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { WebCacheService } from '../cache/web-cache.service';

/**
 * Web cache interceptor — ONLY active in web (SaaS) mode, NOT in Tauri.
 *
 * Caches GET responses in IndexedDB with configurable TTL per route pattern.
 * Mutations (POST/PUT/PATCH/DELETE) automatically invalidate related caches.
 *
 * Strategy:
 *   - Reference data (units, currencies, countries, etc.) : 24h TTL
 *   - Products, customers, suppliers, stocks : 5min TTL
 *   - Ventes, commandes, comptabilite : NEVER cached (toujours frais)
 */

const HOUR = 3_600_000;
const MIN = 60_000;

interface CacheRule {
  pattern: RegExp;
  ttlMs: number;
  group: string;
}

const CACHE_RULES: CacheRule[] = [
  // Reference data — stable, cache 24h
  { pattern: /\/ref-data\/jurisdictions/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/jurisdiction\/[^/?]+\/config/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/currencies/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/countries/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/units/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/warehouse-types/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/zone-types/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/product-types/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/payment-methods/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/delivery-methods/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/movement-types/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/languages/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/product-families/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/variants/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/activity-sectors/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/legal-forms/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/fiscal-identifiers/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/fiscal-regimes/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/payroll-bodies/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/accounting-standards/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/client-types/, ttlMs: 24 * HOUR, group: 'ref-data' },
  { pattern: /\/ref-data\/tax-rules/, ttlMs: 24 * HOUR, group: 'ref-data' },

  // Templates — stable
  { pattern: /\/templates/, ttlMs: 12 * HOUR, group: 'templates' },

  // Products, stocks, customers, suppliers — semi-volatile, cache 5min
  { pattern: /\/products(?:\?|$)/, ttlMs: 5 * MIN, group: 'products' },
  { pattern: /\/products\/[^/]+$/, ttlMs: 5 * MIN, group: 'products' },
  { pattern: /\/stocks(?:\?|$)/, ttlMs: 5 * MIN, group: 'stocks' },
  { pattern: /\/stocks\/[^/]+$/, ttlMs: 5 * MIN, group: 'stocks' },
  { pattern: /\/customers(?:\?|$)/, ttlMs: 5 * MIN, group: 'customers' },
  // Une FICHE (identifiant numérique) — pas `credit-summary` : l'encours
  // change à chaque facture ou paiement, qui n'invalident pas ce groupe.
  { pattern: /\/customers\/\d+$/, ttlMs: 5 * MIN, group: 'customers' },
  { pattern: /\/suppliers(?:\?|$)/, ttlMs: 5 * MIN, group: 'suppliers' },
  { pattern: /\/suppliers\/[^/]+$/, ttlMs: 5 * MIN, group: 'suppliers' },

  // Pricing rules, tax config — semi-stable
  { pattern: /\/pricing-rules/, ttlMs: 10 * MIN, group: 'pricing' },
  { pattern: /\/tax-config/, ttlMs: 10 * MIN, group: 'tax-config' },

  // Zones, warehouses — stable
  { pattern: /\/zones(?:\?|$)/, ttlMs: 30 * MIN, group: 'zones' },
  { pattern: /\/warehouses(?:\?|$)/, ttlMs: 30 * MIN, group: 'warehouses' },

  // Users, roles, permissions — semi-stable
  { pattern: /\/users\/permissions/, ttlMs: 10 * MIN, group: 'permissions' },
  { pattern: /\/plans\/limits/, ttlMs: 10 * MIN, group: 'plans' },

  // Employees, departments — semi-volatile
  { pattern: /\/employees(?:\?|$)/, ttlMs: 5 * MIN, group: 'employees' },
  { pattern: /\/departments(?:\?|$)/, ttlMs: 30 * MIN, group: 'departments' },

  // Chart of accounts, fiscal years — stable
  { pattern: /\/chart-of-accounts/, ttlMs: 30 * MIN, group: 'coa' },
  { pattern: /\/fiscal-years/, ttlMs: 30 * MIN, group: 'fiscal-years' },
];

const INVALIDATION_MAP: { pattern: RegExp; groups: string[] }[] = [
  { pattern: /\/products/, groups: ['products', 'stocks'] },
  { pattern: /\/stocks/, groups: ['stocks'] },
  { pattern: /\/customers/, groups: ['customers'] },
  { pattern: /\/suppliers/, groups: ['suppliers'] },
  { pattern: /\/zones/, groups: ['zones'] },
  { pattern: /\/warehouses/, groups: ['warehouses'] },
  { pattern: /\/employees/, groups: ['employees'] },
  { pattern: /\/pricing-rules/, groups: ['pricing'] },
  { pattern: /\/tax-config/, groups: ['tax-config'] },
  { pattern: /\/chart-of-accounts/, groups: ['coa'] },
  { pattern: /\/fiscal-years/, groups: ['fiscal-years'] },
  { pattern: /\/ref-data/, groups: ['ref-data'] },
];

/**
 * LE LOCATAIRE FAIT PARTIE DE LA CLEF.
 *
 * ── LE DEFAUT, MESURE ──
 *
 * La clef etait `cache:${groupe}:${url}`. Or l'URL ne nomme pas l'entreprise :
 * c'est `x-company-id` qui le fait, et treize types de donnees de reference
 * sont STRICTEMENT par locataire cote serveur
 * [reference-data/src/reference-data.service.ts:59-73], comme le sont
 * `products`, `stocks`, `customers`, `suppliers`, `zones`, `warehouses`,
 * `employees`, `chart-of-accounts` et `users/permissions`.
 *
 * Consequence relevee dans un navigateur reel : une entreprise fraichement
 * creee repond `[]` pour `/ref-data/units`, ce vide est memorise 24 h sous une
 * clef que TOUTES les entreprises lisent, et les unites disparaissent partout.
 * Mesure au moment du diagnostic :
 *
 *   cache:ref-data:.../ref-data/units      ->   0 element,  age 215 min
 *   cache:ref-data:.../ref-data/variants   ->   0 element,  age 216 min
 *
 * alors que la base portait 10 unites et 146 variantes pour l'entreprise
 * affichee. Dans l'autre sens, c'est une FUITE : les donnees d'une entreprise
 * servies a une autre, dans le meme profil de navigateur.
 *
 * ── POURQUOI L'EN-TETE, ET PAS UN SERVICE ──
 *
 * `authInterceptor` s'execute AVANT celui-ci [app.config.ts:136] et a deja
 * pose `x-company-id`. Le lire ici evite une seconde source de verite, et
 * garantit que la clef designe exactement l'entreprise sur laquelle la requete
 * a ete envoyee — pas celle que le stockage local croit selectionnee au moment
 * ou la reponse revient.
 *
 * Les routes globales n'ont pas d'en-tete : elles retombent sur `global`, et
 * gardent le comportement d'avant.
 */
function locataireDe(req: { headers: { get(nom: string): string | null } }): string {
  return req.headers.get('x-company-id') ?? 'global';
}

/**
 * UNE COLLECTION VIDE NE SE MEMORISE PAS.
 *
 * Un `[]` est presque toujours transitoire : provisionnement en cours,
 * mauvais locataire, ensemencement pas encore fait. Le garder 24 h transforme
 * un etat passager en panne d'une journee — c'est exactement ce qui a fait
 * disparaitre les unites et les variantes.
 *
 * Le cout de ne pas le memoriser est UNE requete par visite pour les listes
 * reellement vides. Le cout de le memoriser est une journee de mensonge. Le
 * choix n'est pas serre.
 *
 * Les reponses non tabulaires (objets, nombres) restent memorisees : un objet
 * vide n'a pas le meme sens qu'une liste vide, et rien n'indique qu'il soit
 * transitoire.
 */
function meriteDEtreMemorise(corps: unknown): boolean {
  return !(Array.isArray(corps) && corps.length === 0);
}

function findCacheRule(url: string): CacheRule | null {
  for (const rule of CACHE_RULES) {
    if (rule.pattern.test(url)) return rule;
  }
  return null;
}

export const webCacheInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip in Tauri mode — RxDB handles caching there
  if ((environment as Record<string, unknown>)['isTauri']) {
    return next(req);
  }

  // Skip local assets
  if (req.url.startsWith('./assets/') || req.url.startsWith('/assets/') || req.url.startsWith('assets/')) {
    return next(req);
  }

  const cache = inject(WebCacheService);

  // Mutations → invalidate related caches, then pass through
  if (req.method !== 'GET') {
    for (const entry of INVALIDATION_MAP) {
      if (entry.pattern.test(req.url)) {
        for (const group of entry.groups) {
          cache.invalidateByPrefix(`cache:${group}:`).catch(() => {});
        }
        break;
      }
    }
    return next(req);
  }

  // GET → check cache rule
  const rule = findCacheRule(req.url);
  if (!rule) {
    return next(req);
  }

  const cacheKey = `cache:${rule.group}:${locataireDe(req)}:${req.urlWithParams}`;

  return new Observable(observer => {
    cache.get(cacheKey).then(cached => {
      if (cached !== null) {
        observer.next(new HttpResponse({ status: 200, body: cached }));
        observer.complete();
        return;
      }

      // Cache miss — fetch from network, then store
      next(req).pipe(
        tap(event => {
          if (
            event instanceof HttpResponse &&
            event.status === 200 &&
            meriteDEtreMemorise(event.body)
          ) {
            cache.set(cacheKey, event.body, rule.ttlMs).catch(() => {});
          }
        })
      ).subscribe({
        next: val => observer.next(val),
        error: err => observer.error(err),
        complete: () => observer.complete(),
      });
    }).catch(() => {
      // IndexedDB error — fallback to network
      next(req).subscribe({
        next: val => observer.next(val),
        error: err => observer.error(err),
        complete: () => observer.complete(),
      });
    });
  });
};
